import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import feedbackService from '../../services/feedbackService';
import './BugReportModal.scss';

const BugReportModal = ({ isOpen, onClose }) => {
  const { user, isAuthenticated } = useAuth();
  const { addToast } = useToast();
  const [formData, setFormData] = useState({
    page: '',
    whatWentWrong: '',
    howToReproduce: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await feedbackService.submitBugReport({
        page: formData.page,
        whatWentWrong: formData.whatWentWrong,
        howToReproduce: formData.howToReproduce,
        email: formData.email || (isAuthenticated ? user?.email : null)
      });
      
      setSubmitStatus('success');
      addToast({
        message: 'Thank you! Your bug report has been submitted successfully.',
        type: 'success',
        autoClose: true
      });
      
      // Reset form and close modal after a short delay
      setTimeout(() => {
        onClose();
        setFormData({ page: '', whatWentWrong: '', howToReproduce: '', email: '' });
        setSubmitStatus(null);
      }, 1500);

    } catch (error) {
      console.error('Error submitting bug report:', error);
      setSubmitStatus('error');
      addToast({
        message: error.message || 'Failed to submit bug report. Please try again.',
        type: 'error',
        autoClose: true
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Add animation class management and prevent background scroll
  useEffect(() => {
    if (isOpen) {
      // Get scrollbar width before hiding overflow
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      
      // Apply styles to prevent scrolling while preserving scrollbar space
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      
      // Also apply to any fixed positioned elements that might shift
      const header = document.querySelector('.menu-bar');
      if (header) {
        header.style.paddingRight = `${scrollbarWidth}px`;
      }
    } else {
      // Reset styles
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      
      const header = document.querySelector('.menu-bar');
      if (header) {
        header.style.paddingRight = '';
      }
    }
    
    return () => {
      // Cleanup on unmount
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      
      const header = document.querySelector('.menu-bar');
      if (header) {
        header.style.paddingRight = '';
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="bug-report-modal__overlay" onClick={handleOverlayClick}>
      <div className="bug-report-modal">
        <div className="bug-report-modal__header">
          <h2 className="bug-report-modal__title">Report a Bug</h2>
          <button 
            className="bug-report-modal__close"
            onClick={onClose}
            aria-label="Close bug report modal"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="bug-report-modal__form">
          <div className="bug-report-modal__field">
            <label htmlFor="bug-page" className="bug-report-modal__label">
              What page/section were you on? <span className="bug-report-modal__required">*</span>
            </label>
            <input
              type="text"
              id="bug-page"
              name="page"
              value={formData.page}
              onChange={handleInputChange}
              className="bug-report-modal__input"
              placeholder="e.g. Subnautica Creatures, Profile page, Daily challenges..."
              required
            />
          </div>

          <div className="bug-report-modal__field">
            <label htmlFor="bug-what-wrong" className="bug-report-modal__label">
              What went wrong? <span className="bug-report-modal__required">*</span>
            </label>
            <textarea
              id="bug-what-wrong"
              name="whatWentWrong"
              value={formData.whatWentWrong}
              onChange={handleInputChange}
              className="bug-report-modal__textarea"
              placeholder="Describe what happened that wasn't expected..."
              rows={4}
              maxLength={1000}
              required
            />
            <div className={`bug-report-modal__char-count ${formData.whatWentWrong.length > 1000 ? 'bug-report-modal__char-count--over-limit' : formData.whatWentWrong.length > 900 ? 'bug-report-modal__char-count--warning' : ''}`}>
              {formData.whatWentWrong.length}/1000
            </div>
          </div>

          <div className="bug-report-modal__field">
            <label htmlFor="bug-reproduce" className="bug-report-modal__label">
              How to reproduce (optional)
            </label>
            <textarea
              id="bug-reproduce"
              name="howToReproduce"
              value={formData.howToReproduce}
              onChange={handleInputChange}
              className="bug-report-modal__textarea"
              placeholder="Step-by-step instructions to reproduce the issue..."
              rows={4}
              maxLength={1000}
            />
            <div className={`bug-report-modal__char-count ${formData.howToReproduce.length > 1000 ? 'bug-report-modal__char-count--over-limit' : formData.howToReproduce.length > 900 ? 'bug-report-modal__char-count--warning' : ''}`}>
              {formData.howToReproduce.length}/1000
            </div>
          </div>

          {!isAuthenticated && (
            <div className="bug-report-modal__field">
              <label htmlFor="bug-email" className="bug-report-modal__label">
                Email (Optional)
              </label>
              <input
                type="email"
                id="bug-email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="bug-report-modal__input"
                placeholder="your.email@example.com"
              />
            </div>
          )}

          {submitStatus === 'success' && (
            <div className="bug-report-modal__status bug-report-modal__status--success">
              Thank you! Your bug report has been submitted successfully.
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="bug-report-modal__status bug-report-modal__status--error">
              Sorry, there was an error submitting your bug report. Please try again.
            </div>
          )}

          <div className="bug-report-modal__actions">
            <button
              type="button"
              onClick={onClose}
              className="bug-report-modal__button bug-report-modal__button--secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="bug-report-modal__button bug-report-modal__button--primary"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Bug Report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BugReportModal;